import React from 'react';
import { DockedFooter } from '../../../../../lib/client/components/dockedFooter';

import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    cancel,
    done,
    profileSelectionChanged,
} from './simpleConfigurationPageSlice';

/**
 * This is a root page that implements a simple configuration flow.
 */
export function SimpleConfigurationPage() {
    const profileType = useAppSelector(
        (state) => state.simpleConfigurationPage.profileType,
    );
    const activity = useAppSelector((state) => state.journey.activity);
    const schema = useAppSelector((state) => state.journey.schema);
    const dirty = useAppSelector(
        (state) => state.simpleConfigurationPage.dirty,
    );
    const dispatch = useAppDispatch();

    if (activity === null || schema === null) {
        return (
            <div className="slds-form slds-m-around--medium">
                Waiting for Journey Builder...
            </div>
        );
    }

    return (
        <div className="slds-form slds-m-around--medium">
            <div className="slds-form-element slds-m-bottom_small">
                <label
                    className="slds-form-element__label"
                    htmlFor="profile-type"
                >
                    Select Label
                </label>
                <div className="slds-form-element__control">
                    <div className="slds-select_container">
                        <select
                            className="slds-select"
                            id="profile-type"
                            value={profileType}
                            onChange={(event) =>
                                dispatch(
                                    profileSelectionChanged(event.target.value),
                                )
                            }
                        >
                            <option value="Individual">Individual</option>
                            <option value="Unified Individual">
                                Unified Individual
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <InfoSection />
            <DockedFooter
                canSubmit={() => dirty}
                cancel={() => dispatch(cancel())}
                done={() =>
                    dispatch(
                        done({
                            activity: activity,
                            schema: schema,
                        }),
                    )
                }
            />
        </div>
    );
}

export function InfoSection(): JSX.Element {
    const activity = useAppSelector((state) => state.journey.activity);
    const schema = useAppSelector((state) => state.journey.schema);

    const entries: JSX.Element[] = [];
    if (activity === null || schema === null) {
        const errorMessage = 'Error loading data from Journey Builder';

        entries.push(
            <div className="errorMessage" key="load_error">
                {errorMessage}
            </div>,
        );
    }

    if (activity !== null && schema !== null) {
        const idField = schema.find((s) => s.name === 'Id');
        const dataSourceObjectIdField = schema.find(
            (s) => s.name === 'DataSourceObjectId',
        );

        if (idField === undefined) {
            const errorMessage =
                'This Journey does not appear to have an entry data source activated from Salesforce CDP. This extension is not compatible with this Journey.';

            entries.push(
                <div className="errorMessage" key="no_id_error">
                    {errorMessage}
                </div>,
            );
        }

        if (dataSourceObjectIdField === undefined) {
            const warningMessage =
                'Profile data can be fetched for contacts using this activation, but if multiple contacts have the same IndividualId, the first one will be chosen. To guarantee the expected data source is found, include the DataSourceObjectId in your activation.';

            entries.push(
                <div className="warningMessage" key="no_object_id_warning">
                    {warningMessage}
                </div>,
            );
        }

        const outArgumentReference = `Interaction.${activity.key}.profileData`;
        entries.push(
            <div className="infoMessage" key="activity_key">
                You can refer to the profile JSON in future activities by
                referencing the outArgument: {outArgumentReference}
            </div>,
        );
    }

    return <div id="infoSection">{entries}</div>;
}
